import React, { memo, useCallback, useEffect, useMemo , useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Carousel } from "react-responsive-carousel";
import styled from "@emotion/styled";
import CircleIcon from "@mui/icons-material/Circle";
import InfoIcon from "@mui/icons-material/Info";
import {
  Avatar,
  Box,
  Dialog,
  IconButton,
  InputBase,
  Radio,
  Slide,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import { TransitionProps } from "@mui/material/transitions";
import { DateCalendar, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DemoContainer } from "@mui/x-date-pickers/internals/demo";
import { StaticTimePicker } from "@mui/x-date-pickers/StaticTimePicker";
import dayjs, { Dayjs } from "dayjs";
import TimeButtons from "src/components/Selector/CustomBookingTimeSelector";
import { useAppSelector } from "src/hooks/redux";
import { CloseIcon, PlusIcon, SearchIcon } from "src/Icons";
import { BookingsInfoIcon } from "src/Icons/BookingsInfoIcon";
import { BookingsPayIcon } from "src/Icons/BookingsPayIcon";
import { BookingsPayMasterCardIcon } from "src/Icons/BookingsPayMasterCardIcon";
import { CircleRadioFilledIcon } from "src/Icons/CircleRadioFilledIcon";
import { CircleRadioOutlinedIcon } from "src/Icons/CircleRadioOutlinedIcon";
import { getBookingAvailability, resetBookingAvailabilityParams } from "src/store/reducers/availabilitiesSlice";
import {
  createBookings,
  getBookings,
  updateBookings,
} from "src/store/reducers/bookingsSlice";
import {
  closeBookingsDialog,
  resetBookingsDialogParams,
} from "src/store/reducers/dialogSlice";
import { getEmployees } from "src/store/reducers/employeesSlice";
import { closeModal, showModal } from "src/store/reducers/modalSlice";
import { getServices } from "src/store/reducers/servicesSlice";
import { getStudios } from "src/store/reducers/studiosSlice";
import {
  getFilteredUsers,
  getUsers,
  inviteUser,
  resendUserSms,
  updateSearchParams,
} from "src/store/reducers/usersSlice";
import { generalStyles } from "src/utils/generalStyles/generalStyles";
import { getArrayForBookingSingleSelector } from "src/utils/helpers";
import { getSearchParams } from "src/utils/search";
import { isEmail, isName, isPhoneNumber } from 'src/utils/validation';

import { ButtonAddOutlined } from "../../Button/ButtonAddOutlined";
import { SubmitButton } from "../../Button/SubmitButton";
import { DefaultInput } from "../../Input/DefaultInput";
import { CodeModal } from "../../Modal/BookingsModal/CodeModal";
import { DefaultSingleSelector } from "../../Selector/DefaultSingleSelector";
import { Steppler } from "../../Steppler/Steppler";
import {ClientSearchTypes} from '../../ToggleBox/ToggleBox.types'

interface EmployeeCardShowProp {
  employees: any[];
  employeeId?: string;
  studioId: string;
  serviceId: string;
  rank: any,
  onChange: (employeeId: string, price: number) => void;
}

enum EmployeeRankFilters {
  All = 'All',
  Top = 'Top',
  Master = 'Master',
  Junior = 'Junior',
  Trainee = 'Trainee',
}

const SkillValueByName = {
  [EmployeeRankFilters.Trainee]: 'none',
  [EmployeeRankFilters.Junior]: 'junior',
  [EmployeeRankFilters.Master]: 'master',
  [EmployeeRankFilters.Top]: 'top',
} as any;

const getMatchedSkills = (employee: any, serviceId: any, rank: any) => {
  return employee.skills.filter((skill: any) => {
    if (skill.service.id == serviceId) {
      switch (rank) {
        case EmployeeRankFilters.Trainee:
        case EmployeeRankFilters.Junior:
        case EmployeeRankFilters.Top:
        case EmployeeRankFilters.Master:
          return skill.rank === SkillValueByName[rank];
        default:
          return true;
      }
    } else {
      return false;
    }
  });
};

const EmployeeCardShow = memo(({
  employees,
  onChange,
  studioId,
  serviceId,
  rank,
  employeeId,
}: EmployeeCardShowProp) => {
  const filteredEmployees = employees.filter((employee) => {
    const isFromThisStudio = employee.studios.some(({id}: any) => id == studioId);
    const isMatchesServiceAndRank = getMatchedSkills(employee, serviceId, rank);
    return isFromThisStudio && isMatchesServiceAndRank?.length > 0;
  });

  return (
    <>
      {
        filteredEmployees.map((employee: any) => {
          const skills = getMatchedSkills(employee, serviceId, rank);
          const price = skills?.[0]?.service?.priceList.find((el: any) => skills[0]?.rank === el?.rank)?.price;
          const duration = (() => {
            const timeValue = skills[0].service.duration;
            const hours = Math.floor(timeValue / 60);
            const minutes = timeValue - hours * 60;

            return `${hours}:${minutes} h.`
          })();

          return (
            <Box
              key={employee?.id}
              sx={{
                margin: "0 14px 16px 0",
                borderRadius: "6px",
                width: "233px",
                cursor: "pointer",
                display: "flex",
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: "center",
                padding: "6px 6px",
                boxShadow: "0px 4px 16px 0px rgba(41, 66, 89, 0.12)",
              }}
            >
              <Box sx={{
                display: 'flex',
              }}>
                <Avatar
                  sx={{
                    width: "56px",
                    height: "56px",
                  }}
                  variant="rounded"
                  src={employee?.photo}
                />
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    marginLeft: "12px",
                    maxWidth: "125px",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#080808",
                      fontWeight: 800,
                      fontSize: "14px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {employee?.firstName}
                  </Typography>
                  <Typography
                    sx={{
                      color: "#9D9D9D",
                      fontSize: "12px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {skills[0]?.service?.title}
                  </Typography>

                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "row",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#676767",
                        fontWeight: 700,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {`from ${(price / 100).toFixed(2)}€`}
                    </Typography>
                    <CircleIcon
                      sx={{
                        width: "4px",
                        height: "4px",
                        color: "#D6D6DB",
                        margin: "0 6px",
                      }}
                    />
                    <Typography
                      sx={{
                        fontSize: "12px",
                        color: "#828282",
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {duration}
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Radio
                onClick={() =>
                  onChange(
                    employee.id,
                    price,
                  )
                }
                value={employee.firstName}
                checked={employee.id === employeeId}
                icon={<RadioButton />}
                checkedIcon={<RadioButton checked />}
                disableRipple
                disableFocusRipple
                sx={{
                  padding: 0,
                  height: "21px",
                  margin: "0 0 0 5px",
                }}
              />
            </Box>
          );
        })
      }
    </>
  );
});

const CustomTabs = styled(Tabs)({
  "& .MuiTab-root": {
    fontWeight: 400,
    fontSize: "14px",
    padding: "8px 14px",
    marginTop: "2px",
    color: "#080808",
    border: "1px solid #D6D6DB",
    borderRadius: "40px",
    minWidth: "0",
    minHeight: "0",
    marginRight: "12px",
    "&:last-of-type": {
      marginRight: 0,
    },
  },
  "& .Mui-selected": {
    border: "1px solid #BF295C",
  },
  "& .MuiTabs-indicator": {
    display: "none",
  },
});

export const CustomCalendar = styled(DateCalendar)({
  "&.MuiDateCalendar-root": {
    width: "335px",
    maxHeight: "296px",
    minHeight: "328px",
    margin: 0,
    border: "1px solid #F3F3F3",
    borderRadius: "6px",
  },
  "& .MuiPickersCalendarHeader-root": {
    maxHeight: "21px",
    minHeight: "21px",
    margin: "16px 14px",
    padding: 0,
  },
  "& .MuiDayCalendar-header": {
    borderTop: "1px solid #F3F3F3",
  },
  "& .MuiDayCalendar-header, .MuiDayCalendar-weekContainer": {
    justifyContent: "space-evenly",
  },
  "& .MuiPickersSlideTransition-root": {
    minHeight: "265px",
  },
  "& .MuiPickersDay-root:hover, .MuiPickersDay-root:focus": {
    backgroundColor: "white",
  },
  "& .MuiButtonBase-root.MuiIconButton-root.MuiPickersCalendarHeader-switchViewButton":
    {
      display: "none",
    },
  "& .MuiPickersDay-dayOutsideMonth": {
    color: "#9D9D9D",
  },
  "& .MuiDayCalendar-weekDayLabel": {
    color: "#080808",
    fontWeight: 600,
  },
  "& .MuiPickersCalendarHeader-labelContainer": {
    fontSize: "16px",
    fontWeight: 800,
    color: "#080808",
  },
  "& .MuiIconButton-edgeEnd, .MuiIconButton-edgeStart": {
    color: "#080808",
  },
});

export const RadioButton = ({ checked }: any) => {
  return checked ? (
    <CircleRadioFilledIcon
      sx={{
        fontSize: "20px",
      }}
    />
  ) : (
    <CircleRadioOutlinedIcon
      sx={{
        fontSize: "20px",
      }}
    />
  );
};

const ToggleBox = memo(({ onSelected, label, selected, style }: any) => {
  const toggleBoxStyle = {
    borderRadius: "16px",
    padding: "4px 8px",
    cursor: "pointer",
  };

  const toggleBoxStyleSelected = {
    color: "white",
    backgroundColor: "black",
  };

  const handleChangeSelected = () => {
    onSelected(label)
  }

  return (
    <Box
      sx={[
        toggleBoxStyle,
        selected === label ? toggleBoxStyleSelected : null,
        style,
      ]}
      onClick={handleChangeSelected}
    >
      <Typography fontSize={14}>{label}</Typography>
    </Box>
  );
});

const Transition = React.forwardRef((
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>
) => {
  return <Slide direction="up" ref={ref} {...props} />;
});

const steps = [
  {
    title: "Studio & service",
  },
  {
    title: "Employee & timing",
  },
  {
    title: "Payment method",
  },
];

interface ShowPriceProp {
  currentPrice: number;
}

const ShowPrice = memo(({ currentPrice }: ShowPriceProp) => {
  const oldPrice = 5;
  const priceText = `${currentPrice ? (currentPrice / 100).toFixed(2) : 0} €`;
  return (
    <>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: "20px",
          margin: "24px 0 24px 0",
        }}
      >
        Price
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingBottom: "8px ",
            borderBottom: "1px solid #F3F3F3",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              color: "#828282",
            }}
          >
            Manicure without polish
          </Typography>
          <Typography
            sx={{
              ontWeight: 600,
              fontSize: "16px",
              color: "#080808",
            }}
          >
            {priceText}
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: "8px ",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              color: "#828282",
            }}
          >
            Tax
          </Typography>
          <Typography
            sx={{
              ontWeight: 600,
              fontSize: "16px",
              color: "#080808",
            }}
          >
            0 €
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            paddingTop: "16px",
          }}
        >
          <Typography
            sx={{
              fontSize: "16px",
              color: "#BF295C",
            }}
          >
            Total amount
          </Typography>
          <Typography
            sx={{
              ontWeight: 600,
              fontSize: "16px",
              color: "#BF295C",
            }}
          >
            {priceText}
          </Typography>
        </Box>
      </Box>
    </>
  );
});

const payments = [
  {
    type: "cash",
    title: "Pay at the Studio",
    paymentMethod: "Cash or Card",
    photo: (
      <BookingsPayIcon
        sx={{
          width: "100%",
          height: "100%",
        }}
      />
    ),
  },
  {
    type: "online",
    title: "Online payment",
    paymentMethod: "MasterCard of Visa",
    photo: (
      <BookingsPayMasterCardIcon
        sx={{
          width: "100%",
          height: "100%",
        }}
      />
    ),
  },
];

interface getPaymnetCardsProp {
  payments: any[];
  paymentType: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

const GetPaymentCards = memo(({
  payments,
  paymentType,
  onChange,
}: getPaymnetCardsProp) => {
  return (
    <Box
      sx={{
        display: "flex",
      }}
    >
      {payments.map((payment: any, index: number) => (
        <Box
          key={index}
          sx={{
            display: "flex",
            alignItems: "center",
            boxShadow: "0px 4px 16px 0px rgba(41, 66, 90, 0.12)",
            width: "261px",
            borderRadius: "6px",
            backgroundColor: `${
              paymentType === payment.type ? "#FFF8FA" : null
            }`,
            marginRight: "16px",
          }}
        >
          <Radio
            value={payment.type}
            icon={<RadioButton />}
            onChange={onChange}
            checkedIcon={<RadioButton checked />}
            checked={paymentType === payment.type}
            disableRipple
            disableFocusRipple
            sx={{
              padding: 0,
              marginLeft: "16px",
            }}
          />
          <Box
            sx={{
              padding: "16px 27px 16px 18px",
              display: "flex",
            }}
          >
            <Box
              sx={{
                width: "56px",
                height: "40px",
                margin: "0 7px 0 0",
              }}
            >
              {payment.photo}
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column" }}>
              <Typography
                sx={{
                  fontWeight: 800,
                  fontSize: "14px",
                  color: "#1C1C28",
                }}
              >
                {payment.title}
              </Typography>
              <Typography
                sx={{
                  fontSize: "12px",
                  color: "#858585",
                }}
              >
                {payment.paymentMethod}
              </Typography>
            </Box>
          </Box>
        </Box>
      ))}
    </Box>
  );
});

export const BookingsDialog = ({ isOpen }: any) => {
  const dispatch = useDispatch();

  const { bookingsDialogParams: params } = useSelector(
    (state: any) => state.dialog
  );

  //studios
  const { studios } = useSelector((state: any) => state.studios);
  const studiosToSelect = useMemo(() => {
    const studiosArray :Array<any> = getArrayForBookingSingleSelector(studios);
    return studiosArray;
  },[studios])
  //services
  const { services } = useSelector((state: any) => state.services);
  const servicesToSelect = useMemo(() => {
    const serviceArray:Array<any> = getArrayForBookingSingleSelector(services);
    return serviceArray;
  },[services])
  //employees
  const { employees } = useSelector((state: any) => state.employees);
  //users
  const {
    filteredUsers,
    users,
    selectedToggleBoxValue: toogleBoxValue,
    searchInputValue: searchValue,
  } = useSelector((state: any) => state.users);
  //availability
  const availability = useAppSelector(
    (state: any) => state.availability.bookingDialogAvailability?.data?.availability || []
  )

  const [activeStep, setActiveStep] = useState<number>(params.step);
  const [searchInput, setSearchInput] = useState(searchValue);
  const [isAddNewClientSectionShown, setIsAddNewClientSectionShown] = useState<boolean>(false);
  const [chosenRank, setChosenRank] = useState<string>(EmployeeRankFilters.All);
  const [maxAvailableStepToClick, setMaxAvailableStepToClick] =
    useState<number>(params.maxAvailableStep);
  const [selectedToggleBox, setSelectedToggleBox] =
    useState<string>(toogleBoxValue);

  const formRef = useRef<any>();
  const formClientRef = useRef<any>();

  //Step 1 values
  const [studioId, setStudioId] = useState(params.data.studio.id);
  const [serviceId, setServiceId] = useState(
    params.data.services.length > 0 ? params.data.services[0].service.id : ""
  );
  const [selectedCustomerId, setSelectedCustomerId] = useState<any>(params.isEdit ? params.data.user.id : '');

  //Step new client
  const [isCodeModalOpen, setIsCodeModalOpen] = useState<boolean>(false);
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");

  //Step 2
  const [dateCalendar, setDateCalendar] = useState<Dayjs>(
    params.data.from ? dayjs(params.data.from) : dayjs()
  );

  const [employeeId, setEmployeeId] = useState(params.data.master.id || "");
  const [time, setTime] = useState<string>("");

  //Step 3
  const [price, setPrice] = useState<number>(params.data.price);
  const [paymentType, setPaymentType] = useState<string>(
    params.data.paymentType
  );

  const paramsCatalogue = {
    searchParams: {
      [ClientSearchTypes.PHONE]: "phone",
      [ClientSearchTypes.NAME]: "firstName",
      [ClientSearchTypes.EMAIL]: "email",
    },
  };

  const isContinueButtonAvailable = useMemo(() => {
    const isStudioServiceFulfilled =
      studioId !== undefined &&
      serviceId > 0 &&
      selectedCustomerId;

    const isEmployeeTimmingFulfilled =
      employeeId !== undefined &&
      time.length > 1;

    const isPaymentMethodFulfilled = paymentType.length > 1;

    if (activeStep === 0) {
      return isStudioServiceFulfilled;
    } else if (activeStep === 1) {
      return (
        isStudioServiceFulfilled  &&
        isEmployeeTimmingFulfilled
      );
    } else {
      return (
        isStudioServiceFulfilled &&
        isEmployeeTimmingFulfilled &&
        isPaymentMethodFulfilled
      );
    }
  }, [studioId, serviceId, selectedCustomerId, employeeId, time, paymentType, activeStep]);

  const isSaveButtonValid = useMemo(() => {
    return isContinueButtonAvailable;
  }, [isContinueButtonAvailable]);

  const isNewClientFormValid = useMemo(() => {
    return (
      firstName.length > 1
      && lastName.length > 1
      && isEmail(email)
      && isPhoneNumber(phone)
    );
  }, [firstName, lastName, email, phone]);

  const resetAddNewClient = () => {
    setFirstName('');
    setLastName('');
    setEmail('');
    setPhone('');
    setIsAddNewClientSectionShown(false);
    setIsCodeModalOpen(false);
  }

  const handleAddNewClient = () => {
    dispatch(
      inviteUser({
        firstName,
        lastName,
        phone,
        onSuccess: () => {
          setIsCodeModalOpen(true);
        },
        onFailure: (error: any) => {
          alert(JSON.stringify(error));
        },
      })
    );
  };

  const handleChangeFirstName = useCallback((newValue: string) => {
    setFirstName(newValue);
  },[]);

  const handleChangeLastName = useCallback((newValue: string) => {
    setLastName(newValue);
  },[]);

  const handleChouseEmployee = useCallback((newValue: string, price: number) => {
    setEmployeeId(newValue);
    setPrice(price);
    dispatch(getBookingAvailability(
      {
        employeeId: newValue,
        studioId,
        date: dayjs(dateCalendar).format('YYYY-MM-DD'),
      } as any
    ))
  },[dateCalendar, dispatch, studioId]);

  const handleChousePaymentType = useCallback((
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setPaymentType(event.target.value);
  },[]);

  const handleChangeEmail = useCallback((newValue: string) => {
    setEmail(newValue);
  },[]);

  const handleChangePhone = useCallback((newValue: string) => {
    setPhone(newValue);
  },[]);

  const handleChangeSelectStudioId = useCallback((newValue: any) => {
    setStudioId(newValue);
  },[]);

  const handleChangeDateCalendar = useCallback((newValue: any) => {
    setDateCalendar(newValue);
    const formattedDate = dayjs(newValue).format('YYYY-MM-DD');
    dispatch(
      getBookingAvailability(
        {
          employeeId,
          studioId,
          date: formattedDate,
        } as any
      ))
  },[dispatch, employeeId, studioId]);

  const handleChangeMasterRank = useCallback((
    event: React.SyntheticEvent,
    newValue: any
  ) => {
    setChosenRank(newValue);
  },[]);

  const handleChangeSelectServiceId = useCallback((newValue: any) => {
    setServiceId(newValue);
  },[]);

  const handleChangeStep = (nextStep: number) => {
    setActiveStep(nextStep);
  };

  const handleChangeToggleBox = useCallback((nextSelected: string) => {
    setSelectedToggleBox(nextSelected);
  },[]);

  const handleContinueButtonPress = () => {
    setActiveStep((oldStep) => oldStep + 1);
    setMaxAvailableStepToClick(activeStep + 1);
  };

  const renderUsers = useCallback((usersToShow: any) => {
    return usersToShow.map((user: any) => (
      <Box
        key={user.id}
        sx={{
          paddingRight: " 16px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          height: "64px",
          fontSize: "14px",
          backgroundColor:
            selectedCustomerId === user.id ? "#FFF8FA" : "transparent",
          justifyContent: "space-between",
          "&:hover": {
            backgroundColor: "#FFF8FA",
          },
          cursor: 'pointer',
          borderRadius: '6px',
        }}
        onClick={() => setSelectedCustomerId(user.id)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "35%",
          }}
        >
          <Radio
            icon={<RadioButton />}
            checkedIcon={<RadioButton checked />}
            disableRipple
            disableFocusRipple
            checked={selectedCustomerId === user.id}
            sx={{
              marginRight: "6px",
            }}
            onChange={() => setSelectedCustomerId(user.id)}
          />
          <Avatar
            src={user.imageSrc}
            sx={{ width: 32, height: 32, marginRight: "8px" }}
          />
          {`${user.firstName} ${user.lastName}`}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "20%",
          }}
        >
          {user.phone}
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            width: "35%",
            justifyContent: "end",
          }}
        >
          {user.email}
        </Box>
      </Box>
    ));
  }, [selectedCustomerId, setSelectedCustomerId]);

  const renderUsersTable = useCallback(() => {
    const usersToShow = filteredUsers
    return usersToShow.length > 0 ? (
      renderUsers(usersToShow)
    ) : (
      <Box
        sx={{
          color: "#676767",
          fontStyle: "italic",
          fontSize: "14px",
          border: "1px solid #F3F3F3",
          borderRadius: "6px",
          height: "56px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        Nothing found yet
      </Box>
    );
  }, [searchInput, filteredUsers, users, selectedCustomerId]);

  const renderAddNewClientSection = useCallback(() => {
    return (
      isAddNewClientSectionShown && (
        <Box
          sx={{
            marginTop: "40px",
          }}
        >
          <Typography
            sx={{
              color: "#BF295C",
              textTransform: "uppercase",
              paddingBottom: "24px",
            }}
          >
            New client
          </Typography>
          <form ref={formClientRef}>
            <DefaultInput
              initialValue={firstName}
              label="Name"
              placeholder="name"
              validationFn={isName}
              sx={generalStyles.borderTopRightLeftRadius}
              onInputValueChange={handleChangeFirstName}
            />
            <DefaultInput
              initialValue={lastName}
              label="Surname"
              validationFn={isName}
              placeholder="surname"
              sx={generalStyles.marginTopBotOne}
              onInputValueChange={handleChangeLastName}
            />
            <DefaultInput
              initialValue={email}
              label="Email"
              validationFn={isEmail}
              placeholder="email"
              sx={generalStyles.marginTopBotOne}
              onInputValueChange={handleChangeEmail}
            />
            <DefaultInput
              initialValue={phone}
              label="Phone"
              validationFn={isPhoneNumber}
              placeholder="phone"
              sx={generalStyles.borderBottomLeftRightRadius}
              onInputValueChange={handleChangePhone}
            />
            <SubmitButton
              title="Add new client"
              onClick={handleAddNewClient}
              size="large"
              isDisabled={!isNewClientFormValid}
              fullWidth
              disableRipple
              disableElevation
              variant="contained"
              sx={{marginTop: '10px'}}
            />
          </form>
          <Typography
            sx={{
              display: "flex",
              alignItems: "center",
              marginTop: "16px",
              color: "#828282",
            }}
          >
            <InfoIcon
              sx={{
                width: "15.5px",
                height: "15.5px",
                color: "#B9B9B9",
                marginRight: "8px",
              }}
            />
            Client will recieve verification code on the entered number
          </Typography>
        </Box>
      )
    );
  }, [isAddNewClientSectionShown, firstName, lastName, email, phone, isNewClientFormValid]);

  const showAddNewClientSection = useCallback(() => {
    setIsAddNewClientSectionShown(true);
  },[]);

  const resetDialogData = () => {
    setStudioId("");
    setServiceId("");
    setSelectedCustomerId("");
    setFirstName("");
    setLastName("");
    setPhone("");
    setEmail("");
    setEmployeeId("");
    setTime("");
    setPrice(0);
    setPaymentType("");
  };

  const onClose = () => {
    resetDialogData();
    dispatch(resetBookingsDialogParams());
    dispatch(resetBookingAvailabilityParams());
    dispatch(closeBookingsDialog());
  };

  const handleSaveButtonPress = () => {
    const normalizedTime = `${dayjs(dateCalendar).format('YYYY-MM-DD')}T${time}`;
    const selectedEmployee = employees.find(({id}: any) => id === employeeId);
    const skill = getMatchedSkills(selectedEmployee, serviceId, chosenRank)[0];
    const services = [{
      rank: skill?.rank,
      serviceId,
    }];

    if (params.isEdit) {
      const bookingParams = {
        bookingId: params.data.id,
        paymentType,
        masterId: employeeId,
        studioId,
        from: normalizedTime,
        userId: selectedCustomerId,
        services,
      };

      dispatch(
        updateBookings({
          ...bookingParams,
          onSuccess: () => {
            dispatch(
              showModal({
                modalType: "SUCCESS",
                successText: "Changes successfully saved",
                okButtonTitle: "Ok, I got it",
                onButtonClick: () => {
                  dispatch(getBookings());
                  dispatch(closeModal());
                  onClose();
                },
              })
            );
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    } else {
      dispatch(
        createBookings({
          paymentType,
          masterId: employeeId,
          userId: selectedCustomerId,
          studioId,
          from: normalizedTime,
          services,
          onCreateSuccess: (responseData: any) => {
            dispatch(
              showModal({
                modalType: "SUCCESS",
                successText: `Booking number:
                              ${responseData ? responseData.data.id : ''}
                              Successfully added`,
                okButtonTitle: "Ok, I got it",
                onButtonClick: () => {
                  dispatch(getBookings());
                  dispatch(closeModal());
                  onClose();
                },
              })
            );
          },
          onFailure: (error: any) => {
            dispatch(
              showModal({
                modalType: "DELETE",
                title: `${error?.response?.data?.detail || error.message}`,
                okButtonTitle: "Ok, I got it",
                isButtonCancel: true,
                onButtonClick: () => {
                  dispatch(closeModal());
                },
              })
            );
          },
        })
      );
    }
  };

  const [ searchButton, setSearchButton ] = useState(false);

  const handleSearchClick = useCallback(() => {
    setSearchButton(true);
    dispatch(updateSearchParams({ selectedToggleBox, searchInput }));
    dispatch(
      getFilteredUsers(
        getSearchParams(paramsCatalogue, "", selectedToggleBox, searchInput)
      )
    );
  },[paramsCatalogue, selectedToggleBox, searchInput]);

  const handleSearchInput = useCallback((e:any) => {
    setSearchButton(false)
    setSearchInput(e.target.value)
  },[])

  const handleOpenCodeModal = useCallback(() => {
    setIsCodeModalOpen(false)
  },[])

  const handleCallbackCodeModal = useCallback(() => {
    isCodeModalOpen &&
      dispatch(
        resendUserSms({
          phone,
          onSuccess: () => {},
          onFailure: (error: any) => {
            alert(JSON.stringify(error));
          },
        })
      );
  },[isCodeModalOpen])

  const handleSubmitCodeModal = useCallback(() => {
    resetAddNewClient();
    dispatch(
      showModal({
        modalType: "SUCCESS",
        successText: "User successfully added",
        okButtonTitle: "Ok, I got it",
        onButtonClick: () => {
          dispatch(getUsers());
          dispatch(closeModal());
        },
      })
    );
  },[])

  useEffect(() => {
    dispatch(updateSearchParams({ selectedToggleBox, searchInput }));
    dispatch(getFilteredUsers(getSearchParams(paramsCatalogue, "", selectedToggleBox, searchInput)))
    dispatch(getStudios());
    dispatch(getServices());
    dispatch(getEmployees());
    dispatch(getUsers());
  }, []);


  useEffect(() => {
    // todo little hack for scroll to top after every change active step carousel
    //@ts-ignore
    window.document.querySelector('.MuiPaper-root')?.scrollTo(0, 0);
  }, [activeStep]);

  useEffect(() => {
    params.isEdit && dispatch(getBookingAvailability(
      {
        employeeId,
        studioId,
        date: dayjs(dateCalendar).format('YYYY-MM-DD'),
      } as any
    ))
  }, []);

  return (
    <Dialog
      fullScreen
      open={isOpen}
      onClose={onClose}
      disableEscapeKeyDown
      TransitionComponent={Transition}
    >
      <Box
        display="flex"
        flexDirection="column"
        alignItems="center"
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 5,
          width: "100%",
          backgroundColor: "#fafafa",
          padding: "16px 40px",
        }}
      >
        <Box
          display="flex"
          flexDirection="row"
          alignContent="center"
          justifyContent="space-between"
          sx={{
            width: "100%",
            marginBottom: "16px",
          }}
        >
          <IconButton
            onClick={onClose}
            sx={{
              width: "48px",
              height: "48px",
              borderRadius: "12px",
              backgroundColor: "#F3F3F3",
              color: "#676767",
              fontSize: "26px",
            }}
            disableRipple
          >
            <CloseIcon fontSize="inherit" />
          </IconButton>
          <Box>
            <Typography
              sx={{
                fontSize: "26px",
                fontWeight: "600",
              }}
            >
              {params.isEdit ? "Edit booking" : "Create booking"}
            </Typography>
          </Box>

          {activeStep < steps.length - 1 ? (
            <SubmitButton
              title="Continue"
              disabled={!isContinueButtonAvailable}
              onClick={handleContinueButtonPress}
            />
          ) : (
            <SubmitButton
              title={params.isEdit ? "Save changes" : "Create booking"}
              disabled={!isSaveButtonValid}
              onClick={handleSaveButtonPress}
            />
          )}
        </Box>
        <Steppler
          steps={steps}
          maxAvailableStepToClick={maxAvailableStepToClick}
          activeStep={activeStep}
          onStepChange={handleChangeStep}
        />
      </Box>
      <Box
        sx={{
          backgroundColor: "#1F6AD4",
          color: "white",
          height: "40px",
          display: "flex",
          marginLeft: "-30px",
          justifyContent: "center",
        }}
      >
        <Typography
          sx={{
            fontWeight: 600,
            display: "flex",
            alignItems: "center",
          }}
        >
          <BookingsInfoIcon
            sx={{
              width: "15.5px",
              height: "15.5px",
              color: "white",
              marginRight: "8px",
            }}
          />
          {activeStep === 0 ?
            "You should start with studio selecting followed by service selection" :
            activeStep === 1 ?
              "You should start with employee selection befor picking date and time" :
              "Here You can pick preferable for client payment method, be sure to check service and it`s price"
          }
        </Typography>
      </Box>
      <Carousel
        selectedItem={activeStep}
        showArrows={false}
        showStatus={false}
        showIndicators={false}
        showThumbs={false}
        transitionTime={120}
      >
        {/* Step 1 = Studio&Service Information*/}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            marginLeft: "-3%",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "56px 16px 0",
              width: "761px",
              minWidth: "400px",
            }}
          >
            <Box>
              <form ref={formRef}>
                <Typography
                  sx={{
                    fontSize: "20px",
                    fontWeight: "600",
                    marginTop: "56px",
                    marginBottom: "24px",
                  }}
                >
                  Service & studio
                </Typography>

                <DefaultSingleSelector
                  value={studioId}
                  valuesToSelect={studiosToSelect}
                  placeholder="studio"
                  onChanged={handleChangeSelectStudioId}
                  title="Studio"
                  leftContainerStyle={generalStyles.leftSingleContainerWithUnBottomRadius}
                  rightContainerStyle={generalStyles.rightSingleContainerUnBottomRadius}
                />

                <Box
                  sx={{
                    marginTop: "-1px",
                  }}
                >
                  <DefaultSingleSelector
                    value={serviceId}
                    valuesToSelect={servicesToSelect}
                    placeholder="service"
                    onChanged={handleChangeSelectServiceId}
                    title="Service"
                    leftContainerStyle={generalStyles.leftSingleContainerWithUnTopRadius}
                    rightContainerStyle={generalStyles.rightSingleContainerUnTopRadius}
                  />
                </Box>
              </form>
            </Box>

            <Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginTop: "56px",
                  marginBottom: "24px",
                }}
              >
                Customer
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  flex: 1,
                }}
              >
                <Box
                  sx={{
                    border: "1px solid #D6D6DB",
                    borderBottomLeftRadius: "6px",
                    borderTopLeftRadius: "6px",
                    borderRight: "none",
                    display: "flex",
                    alignItems: "center",
                    paddingX: "12px",
                    paddingY: "12px",
                    color: "#1F1F1F",
                    fontWeight: "500",
                  }}
                >
                  <ToggleBox
                    label="Name"
                    onSelected={handleChangeToggleBox}
                    selected={selectedToggleBox}
                  />
                  <ToggleBox
                    label="Phone"
                    onSelected={handleChangeToggleBox}
                    selected={selectedToggleBox}
                  />
                  <ToggleBox
                    label="Email"
                    onSelected={handleChangeToggleBox}
                    selected={selectedToggleBox}
                  />
                </Box>
                <Box
                  display="flex"
                  sx={{
                    border: "1px solid #D6D6DB",
                    backgroundColor: "#FAFAFA",
                    borderTopRightRadius: "6px",
                    borderBottomRightRadius: "6px",
                    flex: 1,
                  }}
                >
                  <InputBase
                    sx={generalStyles.inputBase}
                    placeholder="Search"
                    onChange={handleSearchInput}
                    value={searchInput}
                  />
                  <IconButton type="button" onClick={handleSearchClick}>
                    <SearchIcon />
                  </IconButton>
                </Box>
              </Box>

              <Typography
                sx={{
                  fontSize: "16px",
                  fontWeight: "500",
                  marginTop: "24px",
                  marginBottom: "16px",
                  textTransform: "uppercase",
                  color: "#4F4F4F",
                }}
              >
                results
              </Typography>

              <Box sx={{
                maxHeight: '260px',
                overflowY: 'auto',
              }}>
                {renderUsersTable()}
              </Box>
              <Box>
                <CodeModal
                  isOpen={isCodeModalOpen}
                  onClose={handleOpenCodeModal}
                  onSubmit={handleSubmitCodeModal}
                  callback={handleCallbackCodeModal}
                />
                {renderAddNewClientSection()}
                {isAddNewClientSectionShown ? null : (
                  <ButtonAddOutlined
                    title="New client"
                    sx={{
                      display: "flex",
                      fontSize: "16px",
                      color: "#BF295C",
                      paddingTop: "18px",
                      paddingBottom: "18px",
                      fontWeight: 600,
                    }}
                    onClick={showAddNewClientSection}
                    rightIcon={<PlusIcon fontSize="inherit" sx={{color: "#BF295C"}} />}
                  />
                )}
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Step 2 = Employee timing*/}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "96px 10px 0",
              width: "761px",
              minWidth: "400px",
            }}
          >
            <Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "600",
                  marginBottom: "24px",
                }}
              >
                Staff
              </Typography>
              <Typography
                sx={{
                  fontWeight: "600",
                  margin: "0 0 10px 0",
                  color: "#828282",
                }}
              >
                Master Rank
              </Typography>
            </Box>
            <Box>
              <CustomTabs
                onChange={handleChangeMasterRank}
                value={chosenRank}
              >
                <Tab label={EmployeeRankFilters.All} value={EmployeeRankFilters.All}  />
                <Tab label={EmployeeRankFilters.Top} value={EmployeeRankFilters.Top} />
                <Tab label={EmployeeRankFilters.Master} value={EmployeeRankFilters.Master} />
                <Tab label={EmployeeRankFilters.Junior} value={EmployeeRankFilters.Junior} />
                <Tab label={EmployeeRankFilters.Trainee} value={EmployeeRankFilters.Trainee} />
              </CustomTabs>
              <Box
                sx={{
                  display: "flex",
                  flexWrap: "wrap",
                  marginTop: "28px",
                }}
              >
                <EmployeeCardShow
                  onChange={handleChouseEmployee}
                  studioId={studioId}
                  serviceId={serviceId}
                  rank={chosenRank}
                  employees={employees}
                  employeeId={employeeId}
                />
              </Box>
            </Box>
            <Box>
              <Typography
                sx={{
                  fontSize: "20px",
                  fontWeight: "600",
                  margin: "54px 0 24px 0 ",
                }}
              >
                Date & time
              </Typography>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  paddingBottom: "54px",
                }}
              >
                <Box>
                  <Typography
                    sx={{
                      fontSize: "16px",
                      fontWeight: "800",
                      marginBottom: "24px",
                    }}
                  >
                    Select date
                  </Typography>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <CustomCalendar
                      disabled={!(employeeId.length > 0)}
                      disablePast
                      dayOfWeekFormatter={(nameofday) => nameofday}
                      showDaysOutsideCurrentMonth
                      value={dateCalendar}
                      onChange={handleChangeDateCalendar}
                      fixedWeekNumber={6}
                      sx={generalStyles.customCalendarMarginTop}
                    />
                  </LocalizationProvider>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    width: "360px",
                  }}
                >
                  <Box>
                    <Typography
                      sx={{
                        fontSize: "16px",
                        fontWeight: "800",
                        marginBottom: "24px",
                      }}
                    >
                        Select time
                    </Typography>
                    <TimeButtons
                      timeRanges={availability}
                      pickedDate={dateCalendar}
                      duration={
                        params.isEdit ?
                          params.data.services[0]?.service?.duration :
                          services.filter((service: any) => service.id === +serviceId)[0]?.duration
                      }
                      onClick={(value: any) => setTime(value)}
                    />
                  </Box>
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
        {/* Step 3 = Payment method*/}
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              padding: "96px 10px 0",
              width: "761px",
              minWidth: "400px",
            }}
          >
            <ShowPrice currentPrice={price} />
            <Typography
              sx={{
                fontWeight: 600,
                fontSize: "20px",
                margin: "61px 0 24px 0",
              }}
            >
              Payment method
            </Typography>

            <GetPaymentCards
              payments={payments}
              paymentType={paymentType}
              onChange={handleChousePaymentType}
            />
          </Box>
        </Box>
      </Carousel>
    </Dialog>
  );
};
